import { useContext, useState, useEffect } from "react";

// react-router-dom components
import { Link, useLocation } from "react-router-dom"; // Added useLocation

// ... existing imports ...
// (We only replaced the imports line above, now finding the next chunk for the logic)

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

// @mui icons
import FacebookIcon from "@mui/icons-material/Facebook";
import GitHubIcon from "@mui/icons-material/GitHub";
import GoogleIcon from "@mui/icons-material/Google";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayoutLanding from "layouts/authentication/components/BasicLayoutLanding";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpg";

import AuthService from "services/auth-service";
import { AuthContext } from "context";
import { useSearchParams } from "react-router-dom";

import CircularProgress from "@mui/material/CircularProgress";
import { GoogleLogin } from "@react-oauth/google";


function Login() {
  const authContext = useContext(AuthContext);
  const location = useLocation(); // Hook to access state passed via navigate
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get("token");

  const [user, setUser] = useState({});
  const [credentialsErros, setCredentialsError] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openReactivateDialog, setOpenReactivateDialog] = useState(false);


  const [inputs, setInputs] = useState({
    email: location.state?.email || "", // Pre-fill email if passed
    password: "",
  });

  const [errors, setErrors] = useState({
    emailError: false,
    passwordError: false,
  });

  const addUserHandler = (newUser) => setUser(newUser);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  // const changeHandler = (e) => {
  //   setInputs({
  //     ...inputs,
  //     [e.target.name]: e.target.value,
  //   });
  // };

  const changeHandler = (e) => {
    const { name, value } = e.target;

    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset field-specific error when user types
    if (name === "email" && errors.emailError) {
      setErrors((prev) => ({ ...prev, emailError: false }));
    }
    if (name === "password" && errors.passwordError) {
      setErrors((prev) => ({ ...prev, passwordError: false }));
    }

    // Optionally clear generic credentials error if user is editing
    if (credentialsErros) {
      setCredentialsError(null);
    }
  };



  const handleGoogleOneTapSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const response = await AuthService.googleOneTapLogin(credentialResponse.credential, invitationToken);
      authContext.login(response.access_token, response.workspaceId || null);
    } catch (error) {
      console.error("Google One Tap Failed:", error);
      // Don't show global error for One Tap silent failure/close, maybe just log it
      // or optionally show a snackbar.
      setLoading(false);
    }
  };

  // Manual Google One Tap Implementation
  useEffect(() => {
    const initializeGoogleOneTap = () => {
      if (!window.google || !window.google.accounts) {
        // console.log("GSI: Google script not loaded yet, retrying...");
        setTimeout(initializeGoogleOneTap, 500);
        return;
      }

      // console.log('GSI: Initializing Manual One Tap...');
      try {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim(),
          callback: handleGoogleOneTapSuccess,
          auto_select: false,
          cancel_on_tap_outside: false,
          context: 'signin'
        });

        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            console.warn('GSI: Prompt not displayed:', notification.getNotDisplayedReason());
          } else if (notification.isSkippedMoment()) {
            console.warn('GSI: Prompt skipped:', notification.getSkippedReason());
          } else {
            // console.log('GSI: Prompt displayed successfully!');
          }
        });
      } catch (err) {
        console.error('GSI: Manual Initialization Error:', err);
      }
    };

    const timer = setTimeout(initializeGoogleOneTap, 1000);
    return () => {
      clearTimeout(timer);
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, []);

  const submitHandler = async (e) => {
    // check rememeber me?
    e.preventDefault();
    setLoading(true); // Disable the button and show spinner
    setCredentialsError(null);

    const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    if (inputs.email.trim().length === 0 || !inputs.email.trim().match(mailFormat)) {
      setErrors({ ...errors, emailError: true });
      setCredentialsError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    if (inputs.password.trim().length < 6) {
      setErrors({ ...errors, passwordError: true });
      setCredentialsError("Password must be at least 6 characters.");
      setLoading(false);
      return;
    }

    const newUser = { email: inputs.email, password: inputs.password };
    addUserHandler(newUser);

    const myData = {
      data: {
        type: "token",
        attributes: { ...newUser, invitationToken },
      },
    };

    try {
      const response = await AuthService.login(myData);

      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }

      const isDemo = inputs.email.trim().toLowerCase() === "live-demo@comparedly.com";
      const redirectPath = isDemo ? "/dashboard" : "/dashboard";

      authContext.login(response.access_token, response.workspaceId || null, redirectPath);
    } catch (res) {
      // ✅ Check for ACCOUNT_DELETED (403)
      if (res?.errors?.[0]?.code === "ACCOUNT_DELETED") {
        setOpenReactivateDialog(true);
        setLoading(false);
        return;
      }

      if (res.hasOwnProperty("message")) {
        setCredentialsError(res.message);
      } else {
        setCredentialsError(res.errors[0].detail);
      }
    } finally {
      if (!openReactivateDialog) {
        setLoading(false); // only stop loading if we aren't opening dialog (or handled in dialog)
      }
    }

    // Clean up inputs (Optional, usually we keep them on error so user can edit)
    // return () => { ... } // Removed cleanup to keep inputs on error
  };

  const handleReactivate = async () => {
    setLoading(true);
    setOpenReactivateDialog(false);

    // Use the same inputs we currently have
    const payload = {
      data: {
        type: "reactivation",
        attributes: { email: inputs.email, password: inputs.password }
      }
    };

    try {
      const response = await AuthService.reactivateAccount(payload);
      // Auto login with the token returned from reactivation
      authContext.login(response.access_token, response.workspaceId || null);
    } catch (error) {
      console.error("Reactivation failed", error);
      setCredentialsError("Failed to reactivate account. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <BasicLayoutLanding image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          pt={1}
          pb={1}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in
          </MDTypography>


          <MDBox mt={2} mb={1} display="flex" justifyContent="center">
            <MDButton
              variant="contained"
              color="info"
              onClick={() => AuthService.initiateFederatedLogin("google", invitationToken)}
              startIcon={<GoogleIcon fontSize="small" />}
              sx={{
                textTransform: "none",
                backgroundColor: "white !important",
                color: "#1f1f1f !important", // Google's specific dark gray
                borderColor: "#747775 !important", // Google's specific border gray
                borderRadius: "10px", // Pill shape
                padding: "8px 24px",  // Proper internal spacing
                fontSize: "14px",
                fontWeight: "500",
                width: "auto",        // Auto width (not full width)
                minWidth: "140px",    // Standard Google button min-width
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "white !important", // Keep white on hover
                  borderColor: "#747775 !important",
                  boxShadow: "0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15) !important", // subtle Google-like shadow
                },
              }}
            >
              Google
            </MDButton>
          </MDBox>

          <MDBox display="flex" alignItems="center" justifyContent="center" width="100%" my={1} px={4}>
            <MDBox flex={1} sx={{ borderBottom: "1px solid white", mx: 1 }} />
            <MDTypography variant="button" color="white">
              or
            </MDTypography>
            <MDBox flex={1} sx={{ borderBottom: "1px solid white", mx: 1 }} />
          </MDBox>

        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" method="POST" onSubmit={submitHandler}>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                variant="standard"
                fullWidth
                value={inputs.email}
                name="email"
                onChange={changeHandler}
                error={errors.emailError}
              // InputLabelProps={{ shrink: Boolean(inputs.email) }}
              // autoComplete="username"
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                variant="standard"
                fullWidth
                name="password"
                value={inputs.password}
                onChange={changeHandler}
                error={errors.passwordError}
              // InputLabelProps={{ shrink: Boolean(inputs.password) }}
              />
            </MDBox>
            {/* <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox> */}
            <MDBox mt={2} mb={1}>
              <MDButton
                variant="contained"
                color="accent"
                fullWidth
                type="submit"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "sign in"}
              </MDButton>
            </MDBox>
            {credentialsErros && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                {credentialsErros}
              </MDTypography>
            )}
            <MDBox mt={0} mb={0} textAlign="center">
              <MDTypography variant="button" color="text">
                Forgot your password? Reset it{" "}
                <MDTypography
                  component={Link}
                  to="/auth/forgot-password"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  here
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mb={0} textAlign="center">
              <MDTypography variant="button" color="text">
                Want to explore?{" "}
                <MDTypography
                  component={Link}
                  to={invitationToken ? `/auth/register?token=${invitationToken}` : "/auth/register"}
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Start Free Trial
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mb={0} textAlign="center">
              <MDTypography variant="button" color="text">
                Go back to{" "}
                <MDTypography
                  component="a"
                  href={process.env.REACT_APP_URL_LANDING || "https://www.comparedly.com"}
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Comparedly
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {/* Reactivation Dialog */}
      <Dialog
        open={openReactivateDialog}
        onClose={() => setOpenReactivateDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Reactivate Account?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This account has been deactivated/deleted. Would you like to reactivate it and log in?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReactivateDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleReactivate} color="primary" autoFocus>
            Reactivate & Login
          </Button>
        </DialogActions>
      </Dialog>
    </BasicLayoutLanding>
  );
}

export default Login;

